
import React, { Fragment, useState, useRef, useCallback, useEffect } from 'react';
import { Collapse, Button, CardBody, Card, Input, Progress, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, CardHeader, CardText, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { toast } from 'components/Shared/Toast';
import { useMutation, useQueryClient } from 'react-query';
import produce from 'immer'
import clsx from 'clsx';
import { noop, parseDate, downloadFile } from 'utils';
import { fetcher } from 'api/client';
import useFileUpload from 'api/hooks/file/useFileUpload';
import { ProfileCan, useProfileAbility } from 'context/profile';
import { contractAction, getPackage } from 'api/actions/contracts';
import { uploadFileV2 as uploadFileAction, detectText, extractINEData } from 'api/actions/file';
import DatePicker from 'components/Shared/DatePicker'
import { useTranslation } from 'react-i18next';

import bootbox from 'components/Modals/Bootbox'
import useContractActions from 'hooks/useContractActions'


function uploadFile({ file }, convertToPdf) {
    return uploadFileAction(file, noop, { 'X-Convert-TO-PDF': convertToPdf });
}

function CollapsableItem({ className, contentClassName, showAction = true, icon, name, children, action, defaultIsOpen = true }) {
    const [isOpen, setIsOpen] = useState(defaultIsOpen);
    const toggle = () => setIsOpen(open => !open);

    return (
        <Card className="mb-2">
            <CardHeader className="">
                <CardText>
                    {icon && <i className={icon}></i>}
                    <span className="ml-2">{name}</span>

                    <div className="float-right">
                        {
                            showAction && action &&
                            <Button size="sm" color="default" className="mr-1" onClick={action?.onClick}>
                                <i className={action.icon} /> {action.name}
                            </Button>
                        }
                        {
                            children &&
                            <Button size="sm" color="default" onClick={toggle}>
                                <i className={`fas ${isOpen ? 'fa-minus' : 'fa-plus'}`} />
                            </Button>
                        }
                    </div>
                </CardText>
            </CardHeader>

            <Collapse isOpen={isOpen} className={contentClassName}>
                {children}
            </Collapse>

        </Card>
    )
}

function AttachmentList({ title, noAttachmentsMessage, contract, attachments = [], attachmentsKey, contractQueryId, participantId, participantIndex }) {
    const { t } = useTranslation();
    const profileAbility = useProfileAbility()
    const queryClient = useQueryClient();
    const inputFileRef = useRef(null);
    const newAttachmentNameRef = useRef('');

    const { mutate: mutateAddAttachment, isLoading } = useMutation((payload) => fetcher(contractAction(contract._id, 'addattachment', payload)), {
        onSuccess: (data, variables) => {
            queryClient.setQueryData(contractQueryId, data.contract)
        },
    })
    const { mutate: mutateUploadFile, isLoading: isLoadingUpload } = useMutation((file) => fetcher(uploadFile(file, true)), {
        onMutate: ({ file, subject }) => {
            queryClient.cancelQueries(contractQueryId)

            const previousValue = queryClient.getQueryData(contractQueryId)

            queryClient.setQueryData(contractQueryId, produce(draft => {
                // Maybe: use something like object-path
                const newAttachment = {
                    filename: file.name,
                    isUploading: true,
                    name: subject
                };

                let arr = draft[attachmentsKey];


                if (participantId) {
                    arr = draft.participants[participantIndex][attachmentsKey];
                }

                if (!arr) arr = []

                arr.push(newAttachment)
            }))

            return previousValue
        },
        onSuccess: ({ url, name, fileId }, { file, subject }) => {

            mutateAddAttachment({
                filename: name,
                subject,
                url,
                fileId,
                participantId,
                isConfidential: attachmentsKey === 'confidentialAttachments'
            })
        },
        onSettled: () => {

            newAttachmentNameRef.current = '';
            inputFileRef.current.value = '';

        },
        onError: (err, variables, previousValue) => {
            queryClient.setQueryData(contractQueryId, previousValue)
        },
    })
    const { mutate: mutateSaveExpireDate } = useMutation((payload) => fetcher(contractAction(contract._id, 'setattachmentexpiry', payload),), {
        onSuccess: async (data) => {
            queryClient.setQueryData(contractQueryId, data.contract)
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    })
    const { mutate: mutateValidateAttachment } = useMutation((payload) => fetcher(contractAction(contract._id, 'validateattachment', payload)), {
        onSuccess: async (data) => {
            queryClient.setQueryData(contractQueryId, data.contract)
        },
    })
    const { mutate: mutateRemoveAttachment } = useMutation((payload) => fetcher(contractAction(contract._id, 'removeattachment', payload)), {
        onMutate: ({ attachmentId, participantId }) => {
            queryClient.cancelQueries(contractQueryId)

            const previousValue = queryClient.getQueryData(contractQueryId)

            queryClient.setQueryData(contractQueryId, produce(draft => {
                let draftArr = draft[attachmentsKey];

                if (participantId) {
                    draftArr = draft.participants[participantIndex][attachmentsKey];
                }

                const index = draftArr.findIndex(attachment => attachment._id === attachmentId)

                if (index !== -1) draftArr.splice(index, 1)
            }))

            return previousValue
        },
        onSuccess: (data, variables) => {
            queryClient.setQueryData(contractQueryId, data.contract)
        },
        onError: (err, variables, previousValue) => {
            queryClient.setQueryData(contractQueryId, previousValue)
        },
    })

    const isUploading = isLoadingUpload || isLoading;

    const handleAttachmentUploadChange = async (evt) => {
        const name = newAttachmentNameRef.current;

        mutateUploadFile({
            file: evt.target.files[0],
            subject: name,
        });
    }

    const handleAttachmentUpload = async () => {
        if (!isUploading) {
            const name = await bootbox.prompt(t('contracts.documents.attachment_name'));
            if (!name || name === '') return toast.error(t('contracts.documents.attachment_name_missing'));
            newAttachmentNameRef.current = name;
            inputFileRef.current.click()
        }

    }

    const handleDelete = (file) => {
        mutateRemoveAttachment({
            attachmentId: file._id,
            participantId
        })
    }

    const handleSaveExpire = (file, expireDate) => {
        mutateSaveExpireDate({
            attachmentId: file._id,
            expire: expireDate,
            participantId,
        })
    }

    const handleValidation = async (file) => {

        await bootbox.dialog({
            message: t('contracts.options.validate_attachment'),
            buttons: {
                cancel: {
                    label: t('contracts.options.cancel'),
                    className: 'none',
                    callback: () => {
                        
                    },
                },
                reject: {
                    label: t('contracts.options.reject'),
                    className: 'btn-danger',
                    callback: async () => {
                        const reason = await bootbox.prompt(t('contracts.options.reject_reason'))

                        if (reason === null) return true;
                        if (reason === '') return false;

                        mutateValidateAttachment({
                            accepted: false,
                            comment: reason,
                            attachmentId: file._id,
                            participantId,
                        })
                    },
                },
                main: {
                    label: t('contracts.options.approve'),
                    className: 'btn-success',
                    callback: async () => {
                        mutateValidateAttachment({
                            validated: true,
                            accepted: true,
                            attachmentId: file._id,
                            participantId,
                        })
                    },
                },
            },
        });
    }


    return (
        <CollapsableItem
            className="extra-documents mb-2"
            icon="fas fa-paperclip"
            name={title}
            showAction={profileAbility.can('modify', contract)}
            action={{
                icon: 'fa fa-fw fa-upload',
                name: t('contracts.documents.upload'),
                onClick: handleAttachmentUpload
            }}
        >
            <Input
                type="file"
                hidden={true}
                accept=".pdf,.docx,.doc,image/*"
                onChange={handleAttachmentUploadChange}
                innerRef={inputFileRef}
            />
            {
                attachments?.map((attachment) => (
                    <FileItem
                        isConfidential={attachmentsKey === 'confidentialAttachments'}
                        key={attachment._id}
                        file={attachment}
                        canValidate={profileAbility.can('validate', contract, participantId ? 'participant-attachments' : 'extra-attachments')}
                        readOnly={profileAbility.cannot('modify', contract)}
                        onDelete={handleDelete}
                        canSetExpire={profileAbility.can('set-expire', contract, participantId ? 'participant-attachments' : 'extra-attachments')}
                        onSaveExpire={handleSaveExpire}
                        onValidation={handleValidation}
                    />
                ))
            }
            {
                !attachments.length &&
                <div className="p-2">
                    {noAttachmentsMessage}
                </div>
            }

        </CollapsableItem>
    )
}



function FileItem({ file = {}, readOnly, canValidate, canSetExpire, onDelete = noop, onSaveExpire = noop, onValidation, isConfidential }) {
    const { t, i18n } = useTranslation();
    const { url, validated, name, expire, isUploading } = file;
    const [expireDate, setExpireDate] = useState(expire)
    const [hasBeenOpened, setHasBeenOpened] = useState(expire)


    const handleDateChange = (date) => {
        setExpireDate(date);
    }

    const handleDelete = () => {
        onDelete(file);
    }

    const handleMarkAsOpen = () => {
        setHasBeenOpened(true);
    }
    const handleSaveExpire = () => {
        onSaveExpire(file, expireDate)
    }

    const handleValidation = () => {

        if (!validated && canValidate && !readOnly) {
            if (!hasBeenOpened) return toast.error(t('errors.MustOpenFirst'), {
                toastId: 'mustOpenFirstAlert'
            });
            onValidation(file)
        }
    }

    return (
        <div className="d-flex align-items-center">
            <div className="p-2">
                {url && !isUploading &&
                    <Button
                        hidden={isConfidential}
                        className="btn-icon-only"
                        color={validated ? 'success' : 'warning'}
                        onClick={handleValidation}
                    >
                        <i className={`fa fa-fw fa-${validated ? 'check' : 'exclamation-triangle'}`}></i>
                    </Button>
                }
            </div>
            <div className="w-100">
                <div className="d-flex justify-content-between">
                    <div>
                        <a href={url} className="text-underline text-break" target="_blank" onClick={handleMarkAsOpen} data-action="mark-as-open">{name}</a>
                    </div>
                    {!readOnly &&
                        <a href="#" onClick={handleDelete} className="">
                            {!isUploading && <span><i className="fa fa-fw fa-times text-danger"></i></span>}
                        </a>
                    }
                    {/* {{#can 'SET_EXTRA_EXPIRE'}} */}
                    {/* <div>
                                <input type="text" data-action="set-expire" placeholder={t('contracts.documents.expire')} />
                                <a href="#" data-action="save-expire">{t('contracts.documents.save')}</a>
                            </div>  */}
                    {
                        isUploading &&
                        <div className="d-flex align-items-center">
                            {/* <Progress animated color="success" value={progress} /> */}
                            <Spinner className="align-self-center" style={{ width: '1rem', height: '1rem' }} />
                            {/* <span className="ml-1"> {t('editor.documents.uploading')}... </span> */}
                        </div>

                    }
                </div>

                {expire && !isUploading ?
                    <div>
                        <span> {t('contracts.documents.expires')}:</span>
                        <span className="ml-2">{parseDate(expire, 'dd/MMM/yyyy')}</span>
                    </div> :
                    <Fragment>
                        {!readOnly && !isUploading && canSetExpire && !isConfidential &&
                            <div className="d-flex">
                                <DatePicker
                                    locale={i18n.language}
                                    className="border-0 border-bottom text-capitalize outline-none"
                                    selected={expireDate ? new Date(expireDate) : null}
                                    onChange={handleDateChange}
                                    minDate={Date.now()}
                                    customInput={<input type="text" data-action="set-expire" style={{ fontSize: 11 }} />}
                                    dateFormat="dd/MMM/yyyy"
                                    placeholderText={t('contracts.documents.expire')}
                                />
                                <a onClick={handleSaveExpire}><small>{t('contracts.documents.save')}</small></a>
                            </div>
                        }
                    </Fragment>
                }

            </div>
        </div>

    )
}

function Documents({ contract = {}, listOnly, refetch, contractQueryId }) {
    const { t } = useTranslation();
    const [isExtractorOpen, setIsExtractorOpen] = useState(false);
    const profileAbility = useProfileAbility()
    const canCreateShareDocsProtected = profileAbility.can('CREATE_SHAREDOCS_PROTECTED');
    const downloadAction = useContractActions(contract, 'DOWNLOAD')
    const { mutate: downloadMutation, isLoading: isDownloading } = useMutation(() => fetcher(getPackage({ contractId: contract._id })), {
        onSuccess: (data) => {
            downloadFile({
                name: t('contracts.documents.contract'),
                blob: new Blob([data], { type: 'application/zip' }),
                ext: 'zip'
            })();
        },
        onError: (error) => {
            toast.error(`errors.${error.message}`)
        }
    })

    const handleDownloadPackage = () => {
        // const contractName = contract.name;
        // const text = t('contracts.documents.attachment_download', { replace: { contractName } });
        // // const dialog = await bootbox.dialog({
        // //     text,
        // //     closeButton: false
        // // });
        downloadMutation()
    }

    const toggleExtractor = () => setIsExtractorOpen(isOpen => !isOpen)

    return (
        <div className="contract-documents active p-2 overflow-y-auto">
            <div className="detail">
                <p>{t('contracts.documents.description')}</p>
            </div>
            <div className="document-list">

                <div className="participant-documents"></div>
                {/* <button className="btn btn-sm btn-primary mb-1" onClick={toggleExtractor}>{t('editor.documents.extract_ine')}</button> */}
                <INEExtractorModal isOpen={isExtractorOpen} toggle={toggleExtractor} />
                {
                    contract.participants?.map((participant, index) => (
                        <AttachmentList
                            key={participant._id}
                            contract={contract}
                            attachments={participant.attachments}
                            participantId={participant._id}
                            participantIndex={index}
                            attachmentsKey={'attachments'}
                            contractQueryId={contractQueryId}
                            title={`${participant.label} - ${participant.name}`}
                            noAttachmentsMessage={t('contracts.documents.participant_empty')}
                        />
                    ))
                }
                {
                    contract.participants?.map((participant, index) => {

                        if (!participant.veridoc?.veridocId && !participant.videoStatus && !participant.tuIdentidad?.ine) {
                            return null
                        }

                        return (
                            <CollapsableItem
                                key={participant._id}
                                className="contract-validations mb-2"
                                icon="fas fa-vial"
                                contentClassName="p-1 border"
                                name={`${participant.label} - Validaciones`}
                            >
                                <Shield3VideoItem
                                    contract={contract}
                                    participant={participant}
                                />
                                <VeridocValidationItem
                                    contractId={contract._id}
                                    participantId={participant._id}
                                    veridoc={participant.veridoc}
                                />
                                <TuIdentidadValidationItem
                                    contractId={contract._id}
                                    participantId={participant._id}
                                    tuIdentidad={participant.tuIdentidad}
                                />
                            </CollapsableItem>
                        )
                    })
                }
                <AttachmentList
                    contract={contract}
                    attachments={contract.extraAttachments}
                    attachmentsKey="extraAttachments"
                    contractQueryId={contractQueryId}
                    title={t('contracts.documents.additional')}
                    noAttachmentsMessage={t('contracts.documents.additional_empty')}
                />
                { contract.requireVideoDeclaration && (!!contract?.zoom?.recordings?.share_url || !!contract?.zoom?.localRecordingUrl) &&
                    <CollapsableItem
                        className="extra-documents mb-2"
                        icon="fas fa-paperclip"
                        name="Zoom"
                    >
                        <div className="p-2">
                            <a target="_blank" href={contract?.zoom?.recordings?.share_url || contract?.zoom?.localRecordingUrl} rel="noreferrer">Video</a>
                        </div>
                     </CollapsableItem>
                    }
                {canCreateShareDocsProtected && <AttachmentList
                    contract={contract}
                    attachments={contract.confidentialAttachments}
                    attachmentsKey="confidentialAttachments"
                    contractQueryId={contractQueryId}
                    title={t('contracts.documents.confidential')}
                    noAttachmentsMessage={t('contracts.documents.confidential_empty')}
                />}
                {/* <CollapsableItem 
                    className="contract-versions mb-2" 
                    icon="fas fa-users" 
                    name={t('contracts.documents.versions')}
                >
                   {
                        contract?.changelog?.map((change, index) => {
                            const title = change.type === 'final' ? t('contracts.documents.version_final') 
                                : change.type === 'version' ? `${t('contracts.documents.version')} ${index + 1}` 
                                : change.type;
                            return (
                                <DocumentVersion 
                                    title={title}
                                    date={change.createdAt}
                                    user={change.user}
                                    url={change.url}
                                />
                            )
                        })
                   }
                   {
                       !contract?.changelog?.length && 
                       <div className="p-2">
                           {t('contracts.documents.versions_empty')}
                       </div>
                   }
                </CollapsableItem> */}
                {contract.wordonline &&
                    <CollapsableItem
                        className="wordonline-versions mb-2"
                        icon="fas fa-file-word-o"
                        name={`${t('contracts.documents.versions')} WordOnline`}
                    >
                        {
                            contract?.wordonline?.versions?.map((version, index) => {
                                const url = `https://drive.google.com/viewerng/viewer?url=${encodeURI(version.url)}&embedded=true&test=1`;
                                return (
                                    <DocumentVersion
                                        key={version._id}
                                        date={version.modified}
                                        user={version.user}
                                        url={url}
                                    />
                                )
                            })
                        }
                        {
                            !contract?.wordonline?.versions?.length &&
                            <div className="p-2">
                                {t('contracts.documents.versions_empty')}
                            </div>
                        }
                    </CollapsableItem>
                }
                <CollapsableItem
                    className="contract-references mb-2"
                    contentClassName="p-1"
                    icon="fas fa-users"
                    name={t('contracts.documents.references')}
                >
                    {contract?.reference?.map((reference) => {

                        return (
                            <ContractReferenceItem key={reference._id} reference={reference} onUpdateReference={refetch} />
                        )
                    })}
                    {
                        !contract?.reference?.length &&
                        <div>
                            {t('contracts.documents.references_empty')}
                        </div>
                    }
                </CollapsableItem>
                {downloadAction && <CollapsableItem
                    className="contract-items mb-2"
                    icon="fas fa-file"
                    name={t('contracts.documents.contract')}
                    action={{
                        icon: 'fa fa-fw fa-download',
                        name: t('contracts.documents.download'),
                        onClick: downloadAction?.onClick
                    }}
                />}

                <ProfileCan I="download-package" this={contract}>
                    <Button color="secondary" className="font-weight-bold" block size="sm" onClick={handleDownloadPackage} disabled={isDownloading}>
                        {isDownloading ?
                            <Spinner size="sm"/>:
                            <i className="fas fa-cube"></i>
                        }
                        {' '}{t('contracts.documents.package')}
                    </Button>
                </ProfileCan>
            </div>
        </div>
    )
};

function ContractReferenceItem({ reference, onUpdateReference }) {
    const { t } = useTranslation()
    const contractActions = useContractActions(reference.document, null, {
        onUpdateContract: () => {
            onUpdateReference()
        },
        returnAsObject: true,
        refetch: onUpdateReference,
    })
    const getColor = useCallback((action) => {
        const colorObj = {
            EDIT: 'primary',
            SHOW: 'info',
            UNLINK_REFERENCE: 'danger'
        }
        if (action?.name?.includes('DOWNLOAD')) {
            return 'success'
        }

        return colorObj?.[action.name] ?? 'primary'

    }, [])

    return (
        <div className="d-flex align-items-center">
            <div>
                {/* <Button className="p-1" size="sm" color={reference.validated ? 'success' : 'warning'} className="btn-icon-only">
            <i className={`fas fa-${reference.validated ? 'check' : 'exclamation-triangle'}`}></i>
        </Button> */}
            </div>
            <div className="d-flex p-1 w-100 justify-content-between">
                <a href="#" className="text-underline align-self-center" onClick={contractActions['EDIT'].onClick}>{reference.document.name}</a>
                <ProfileCan I="modify" this={reference.document}>
                    <UncontrolledDropdown>
                        <DropdownToggle className="px-2 py-0" caret color='default'>
                            <span className="caret"></span>
                        </DropdownToggle>
                        <DropdownMenu className="p-1" tag="ul">
                            {
                                Object.values(contractActions).filter(action => !['RENAME', 'CANCEL'].includes(action.name)).map((action) => (
                                    <DropdownItem key={action.label} tag="li" className="hover_text-decoration-underline mb-1" onClick={action.onClick}><i className={`fas fa-${action.faIcon} text-${getColor(action)}`}></i> {t(action.label)}</DropdownItem>
                                ))
                            }
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </ProfileCan>
            </div>
        </div>
    )
}

function DocumentVersion({ title, url, user, date }) {
    return (
        <div className="p-3 d-flex">
            { title && <small> {title} </small>}
            <small>{parseDate(date)} - {user}</small>
            <a href={url} className="ml-auto" target="_blank" rel="noreferrer">
                <i className="fas fa-eye"></i>
            </a>
        </div>
    )
}

export default Documents;

function VeridocValidationItem({ veridoc = {}, participantId, contractId }) {
    const { veridocId, report, validated, status, result = {} } = veridoc;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { mutate: mutateValidate } = useMutation((payload) => fetcher(contractAction(contractId, 'validateveridoc', payload)), {
        onSuccess: (data) => {
            queryClient.setQueryData(['contract', contractId], data.contract)
        }
    })

    if (!veridocId) {
        return null;
    }

    const handleValidation = async () => {
        mutateValidate({ participantId, contractId })
    }

    return (
        <CollapsableItem
            className="contract-versions mb-2"
            contentClassName="test border rounded"
            icon="fas fa-id-card"
            defaultIsOpen={false}
            name={'Identificación'}
        >
            <div className="participant-veridoc-item p-2 border-top">
                {report &&
                    <Button
                        tag="a"
                        className="w-100 mb-3"
                        href={report}
                        size="sm"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <strong>{t('editor.documents.download_report')}</strong>
                    </Button>
                }
                {!validated &&
                    <Button
                        className="w-100 mb-3"
                        size="sm"
                        onClick={handleValidation}
                    >
                        <strong>{t('editor.documents.validate')}</strong>
                    </Button>
                }
                <VeridocProperty valueClassName="text-dark" label={t('editor.documents.status')} value={t(`editor.documents.statuses.${status || 'validating'}`)} loading={!status} />
                {/* <p>identifier: {result.idenfier}</p>
            <p>frontImage: {result.frontImage}</p>
            <p>backImage: {result.backImage}</p>
            <p>documentPortraitImage: {result.documentPortraitImage}</p>
            <p>faceImage: {result.faceImage}</p>
            <p>documentData: {result.documentData}</p> */}
                <VeridocProperty label={t('editor.documents.total_validations')} value={result.totalChecks} />
                <VeridocProperty label={t('editor.documents.successful_validations')} value={result.successChecks} />
                <VeridocProperty label={t('editor.documents.warnings')} value={result.warningChecks} />
                <VeridocProperty label={t('editor.documents.unsuccessfull_validations')} value={result.failedChecks} />
                {/* <VeridocProperty label="expertUsed" value={result.expertUsed} /> */}
                <VeridocProperty label={t('editor.documents.validation_time')} value={result.checkTime} />
                <VeridocProperty label={t('editor.documents.comments')} value={result.expertComments} />
                <VeridocProperty label={t('editor.documents.origin')} value={result.sourceType} />
                <VeridocProperty label={t('editor.documents.user')} value={result.userName} />
                {/* <VeridocProperty label="spentCredits" value={result.spentCredits} /> */}
                <VeridocProperty label={t('editor.documents.start_date')} value={parseDate(result.startDateUtc)} />
                <VeridocProperty label={t('editor.documents.end_date')} value={parseDate(result.endDateUtc)} />
                <VeridocProperty label={t('editor.documents.security_level')} value={result.securityLevel} />
                <VeridocProperty label={t('editor.documents.description_security_level')} value={result.securityLevelDescription} />
                <VeridocProperty label={t('editor.documents.global_result')} value={result.globalResult} />
                <VeridocProperty label={t('editor.documents.description_result')} value={result.globalResultDescription} />
                <VeridocProperty label={t('editor.documents.language')} value={result.language} />
                <VeridocProperty label={t('editor.documents.type')} value={result.documentType} />
                <VeridocProperty label={t('editor.documents.country_code')} value={result.countryCode} />
                <VeridocProperty className="mb-5" label={t('editor.documents.template_name')} value={result.templateName} />

                {/* <button 
                type="button" 
                className="btn btn-default mb-2 w-100 text-left" 
                data-toggle="collapse" data-target="#docVer">
                    <small>Verificaciones:</small>
            </button> */}
                <CollapsableItem
                    className="veridoc-verifications mb-2"
                    contentClassName="test border rounded mb-2 py-2"
                    defaultIsOpen={false}
                    name={t('editor.documents.validations')}
                >
                    <ul className="mb-0">
                        {result.documentData?.map(data => {
                            return (
                                <li key={data.name}>
                                    <small><b>{data?.name ?? data?.type}({data.source}):</b></small><br />
                                    <span>{data.value}</span>
                                </li>
                            )
                        })}
                    </ul>
                </CollapsableItem>
                {result.documentVerifications?.map(verification => {
                    return (
                        <CollapsableItem
                            key={verification.name}
                            className="veridoc-document-verifications mb-2"
                            contentClassName="test border rounded mb-2 py-2"
                            defaultIsOpen={false}
                            name={`${verification.name} (${verification.category}):`}
                        >
                            <ul className="mb-0 list-style-none">
                                <li><small>{t('editor.documents.result')}: {verification.result} - {verification.output}</small></li>
                            </ul>
                        </CollapsableItem>
                    )
                })}

            </div>
        </CollapsableItem>
    )
}
function TuIdentidadValidationItem({ tuIdentidad = {}, participantId, contractId }) {
    const { report, validated, status, ine = {}, images, faceMatch } = tuIdentidad;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { mutate: mutateValidate } = useMutation((payload) => fetcher(contractAction(contractId, 'validateTuIdentidad', payload)), {
        onSuccess: (data) => {
            queryClient.setQueryData(['contract', contractId], data.contract)
            toast.success(t('success'))
        }
    })

    if (!Object.keys(ine).length) {
        return null;
    }

    const handleValidation = async () => {
        mutateValidate({ participantId, contractId })
    }

    return (
        <CollapsableItem
            className="contract-versions mb-2"
            contentClassName="test border rounded"
            icon="fas fa-id-card"
            defaultIsOpen={false}
            name={'Identificación'}
        >
            <div className="participant-veridoc-item p-2 border-top">
                {report &&
                    <Button
                        tag="a"
                        className="w-100 mb-3"
                        href={report}
                        size="sm"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <strong>{t('editor.documents.download_report')}</strong>
                    </Button>
                }
                {!validated &&
                    <Button
                        className="w-100 mb-3"
                        size="sm"
                        color="primary"
                        onClick={handleValidation}
                    >
                        <strong>{t('editor.documents.validate')}</strong>
                    </Button>
                }
                <Button
                    className="mb-3"
                    size="sm"
                    tag="a"
                    color="light"
                    hidden={!images.ineFront}
                    href={images.ineFront}
                    target="_blank"
                    rel="noreferrer"
                >
                    <strong>INE Anverso</strong>
                </Button>
                <Button
                    className="mb-3"
                    size="sm"
                    tag="a"
                    color="light"
                    hidden={!images.ineBack}
                    href={images.ineBack}
                    target="_blank"
                    rel="noreferrer"
                >
                    <strong>INE Reverso</strong>
                </Button>
                <Button
                    className="mb-3"
                    size="sm"
                    tag="a"
                    color="light"
                    hidden={!images.selfie}
                    href={images.selfie}
                    target="_blank"
                    rel="noreferrer"
                >
                    <strong>Rostro</strong>
                </Button>
            
                {faceMatch && <VeridocProperty label="Porcentaje de confianza" value={`${(faceMatch.confidence * 100).toFixed(2)}%`} /> }
                <VeridocProperty label="Nombre" value={ine.data.name} />
                <VeridocProperty label="Apellido Paterno" value={ine.data.firstLastName} />
                <VeridocProperty label="Apellido Materno" value={ine.data.secondLastName} />
                <VeridocProperty label="Sexo" value={ine.data.sex} />
                <VeridocProperty label="CURP" value={ine.data.curp} />
                <VeridocProperty label="Clave de Elector" value={ine.data.electoralId} />
                <VeridocProperty label="Domicilio" value={`${ine.data.addressLine1} ${ine.data.addressLine2} ${ine.data.addressLine3}`} />
                <VeridocProperty label="Fecha de Nacimiento" value={ine.data.dateOfBirth} />
                <VeridocProperty label="Fecha de Vencimiento" value={ine.data.expirationDate} />

            </div>
        </CollapsableItem>
    )
}

function VeridocProperty({ className, valueClassName, label, value, loading = false }) {
    return (
        <div className={className}>
            <small className="d-inline-block font-weight-bold mr-2">{label}:</small>
            {loading && <i className="fas fa-spinner fa-spin"></i>}
            <span className={clsx('font-weight-thin', !valueClassName && 'text-muted', valueClassName)}>{value}</span>
        </div>
    )
}

function Shield3VideoPreview({ previewVideo, toggle, video }) {
    return (
        <Modal isOpen={previewVideo} centered size="md">
            <ModalHeader toggle={toggle} />
            <ModalBody>
                <video controls className="w-100">
                    <source src={video} type="video/webm" />
                </video>
            </ModalBody>
        </Modal>
    )
}

function Shield3VideoItem({ participant, contract = {} }) {
    const { t } = useTranslation(['translation', 'glossary']);
    const [previewVideo, setPreviewVideo] = useState(false);
    const toggle = () => {
        setPreviewVideo(!previewVideo);
    }
    const queryClient = useQueryClient();
    const { _id: participantId, video, videoValidated, videoStatus } = participant;
    const { mutate: mutateValidateVideo } = useMutation(({ contractId, ...payload }) => fetcher(contractAction(contractId, 'validateparticipantvideo', payload)), {
        onSuccess: (data) => {
            if (data.success) queryClient.setQueryData(['contract', contract._id], produce(draft => {
                draft.participants.find(p => p._id === participantId).videoValidated = true;
            }))
        }
    })
    const { mutate: mutateRejectVideo } = useMutation(({ contractId, ...payload }) => fetcher(contractAction(contractId, 'rejectparticipantvideo', payload)), {
        onSuccess: (data) => {
            toast.success(t('editor.action.video_rejected'))
            if (data.success) queryClient.setQueryData(['contract', contract._id], produce(draft => {
                const party = draft.participants.find(p => p._id === participantId)
                party.videoValidated = false;
                party.videoStatus = 'rejected'
                party.video = undefined;
            }))
        }
    })

    if (!videoStatus) {
        return null;
    }

    const handleValidation = async () => {
        if (videoStatus === 'validating' && contract.shield3ValidationRequired) {
            const contractId = contract._id;

            bootbox.dialog({
                message: t('contracts.options.validate_attachment'),
                buttons: {
                    cancel: {
                        label: t('contracts.options.cancel'),
                        className: 'none',
                        callback: () => {
                            bootbox.hideAll();
                        },
                    },
                    reject: {
                        label: t('contracts.options.reject'),
                        className: 'btn-danger',
                        callback: async () => {
                            mutateRejectVideo({
                                participantId,
                                contractId
                            });
                        },
                    },
                    main: {
                        label: t('contracts.options.approve'),
                        className: 'btn-success',
                        callback: async () => {
                            mutateValidateVideo({
                                participantId,
                                contractId
                            });

                        },
                    },
                }
            })
        }
    }

    const handleOpenVideo = async () => {
        toggle()

    }

    return (
        <CollapsableItem
            className="contract-versions mb-2"
            contentClassName="test border rounded p-2"
            icon="fas fa-camera"
            name={`${t('glossary:shield', { context: 'number', number: 3 })} Video`}
        >
            <Shield3VideoPreview previewVideo={previewVideo} toggle={toggle} video={video} />
            <div className="d-flex align-items-center">
                <div hidden={!contract.shield3ValidationRequired}>
                    <Button
                        className="btn-icon-only p-1"
                        color={videoValidated ? 'success' : videoStatus === 'rejected' ? 'danger' : 'warning'}
                        onClick={handleValidation}
                    >
                        <i className={`fa fa-fw fa-${videoValidated ? 'check' : videoStatus === 'rejected' ? 'times' : 'exclamation-triangle'}`}></i>
                    </Button>
                </div>
                <div className="ml-1 pl-1">
                    <a
                        href="#"
                        className="text-underline"
                        onClick={handleOpenVideo}
                    >
                        {t('editor.documents.acceptance_video')}
                    </a>
                </div>
            </div>

        </CollapsableItem>)
}

function INEExtractorModal({ isOpen, toggle }) {
    const [data, setData] = useState({});
    const [status, setStatus] = useState('upload');
    const { t } = useTranslation();
    const [file, setFile] = useState({});
    const { mutate: mutateExtractINEData, reset: extractReset, isLoading: extractLoading, isSuccess: extractSuccess } = useMutation((payload) => fetcher(extractINEData(payload)), {
        onSuccess: (data, variables) => {
            console.log('extract info')
            setData(data.ineData)
        }
    });
    const { mutate: mutateDetectText, reset: detectReset, isLoading: detectLoading, isSuccess: detectSuccess } = useMutation((payload) => fetcher(detectText(payload)), {
        onSuccess: (data, variables) => {
            console.log('extract info')
            mutateExtractINEData({ fileId: variables.fileId })
        }
    });
    const { uploadFileState, chooseFileAndUploadFile } = useFileUpload({
        onSuccess: async (file, variables) => {
            setFile(file);
            setData({})
            mutateDetectText({ fileId: file.fileId })
            // mutateExtractINEData({ fileId: "5ffdd99f8ec8787de86a4441" })
        },
    })

    const handleUpload = () => {
        chooseFileAndUploadFile({ }, {
            accept: 'image/*',
        })
        extractReset.reset()
        // mutateExtractINEData({ fileId: "5ffdd99f8ec8787de86a4441" })
    }

    const handleClose = () => {
        setFile({})
        setData({})
        extractReset.reset()
        uploadFileState.reset()
        detectReset.reset()
        toggle()
    }

    console.log(data)

    if (!isOpen) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} centered>
            <ModalHeader toggle={handleClose}>
                INE
            </ModalHeader>
            <ModalBody style={{ overflow: 'scroll', top: 40 }}>
                <div className="px-2">
                    <button className="btn btn-primary" onClick={handleUpload}>Subir INE</button>
                </div>

                {
                    uploadFileState.isLoading &&
                    <div>{t('editor.documents.uploading')}...</div>
                }
                {uploadFileState.isSuccess &&
                    <div className="d-flex">
                        <div className="p-2">
                            <img src={file.url} width={300} alt="INE" />
                        </div>
                        {
                            (extractLoading || detectLoading) &&
                            <div className="p-2">{t('editor.documents.extracting_information')}...</div>
                        }
                        {
                            extractSuccess &&
                            <div className="p-2">
                                <INEProperty label="Apellido(s)" value={`${data.lastName ?? ''} ${data.lastName2 ?? ''}`} />
                                <INEProperty label="Nombre(s)" value={data.name} />
                                <INEProperty label="Sexo" value={data.gender} />
                                <INEProperty label="Fecha de nacimiento" value={data.birthDate} />
                                <INEProperty label="Domicilio" value={data.address} />
                                <INEProperty label="CURP" value={data.curp} />
                                <INEProperty label="Clave de elector" value={data.claveElector} />
                            </div>
                        }
                    </div>
                }
            </ModalBody>
        </Modal >
    )
}

function INEProperty({ label, value }) {
    return (
        <div>
            <strong>{label}:</strong> {value}
        </div>
    )
}